/**
 * `/account/followers` (ISSUE-1372): the fan's Requests, Followers and
 * Following, v1's Settings, Community, Followers screen. The active tab is
 * the `tab` search param, so back and a shared link land on the right one.
 * With no `tab`, the screen opens on Requests when any are waiting and on
 * Followers otherwise. Named `account_.followers` so it stays out of
 * `/account`'s tree, the same as `account_.edit.tsx`.
 */
import { useFanFollowRequestCount } from "@sportsmet/profile-sdk/private";
import { Tabs } from "@sportsmet/ui";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  FOLLOWERS_BACK_LABEL,
  FOLLOWERS_HEADING,
  FOLLOWERS_TAB_LABEL,
  FOLLOWERS_TABS_LABEL,
  FOLLOWING_TAB_LABEL,
  REQUESTS_TAB_LABEL,
} from "../features/followers/followCopy";
import { FollowersTab } from "../features/followers/FollowersTab";
import { FollowingTab } from "../features/followers/FollowingTab";
import { RequestsTab } from "../features/followers/RequestsTab";
import {
  FOLLOWERS_TABS,
  type typeFollowersTab,
  validateFollowersSearch,
} from "../routeState/sectionSearch";
import { BACK_LINK_CLASS } from "../shell/BackLink";
import { SectionScreen } from "../shell/SectionScreen";

function isFollowersTab(value: string): value is typeFollowersTab {
  return (FOLLOWERS_TABS as readonly string[]).includes(value);
}

const FollowersScreen = (): ReactNode => {
  const search = useSearch({ from: "/account_/followers" });
  const navigate = useNavigate({ from: "/account/followers" });
  const countQuery = useFanFollowRequestCount({});
  const requestCount = countQuery.data?.data?.requestCount ?? 0;

  const activeTab: typeFollowersTab =
    search.tab ?? (requestCount > 0 ? "requests" : "followers");

  return (
    <SectionScreen
      heading={FOLLOWERS_HEADING}
      back={
        <Link
          to="/account"
          className={BACK_LINK_CLASS}
          data-testid="followers-back-link"
        >
          {FOLLOWERS_BACK_LABEL}
        </Link>
      }
    >
      <Tabs
        label={FOLLOWERS_TABS_LABEL}
        value={activeTab}
        fillOnPhone
        onValueChange={(next) => {
          if (!isFollowersTab(next)) return;
          void navigate({ search: { tab: next }, replace: true });
        }}
        items={[
          {
            value: "requests",
            label: REQUESTS_TAB_LABEL,
            badge: requestCount > 0 ? requestCount : undefined,
            content: <RequestsTab />,
          },
          {
            value: "followers",
            label: FOLLOWERS_TAB_LABEL,
            content: <FollowersTab />,
          },
          {
            value: "following",
            label: FOLLOWING_TAB_LABEL,
            content: <FollowingTab />,
          },
        ]}
      />
    </SectionScreen>
  );
};

export const Route = createFileRoute("/account_/followers")({
  beforeLoad: requireSignedIn,
  validateSearch: validateFollowersSearch,
  component: FollowersScreen,
});
